import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface FinanceData {
  revenue: number;
  expenses: number;
  profit: number;
  revenueChange: number;
  expensesChange: number;
  lastUpdated: Date;
}

export const useFinanceData = () => {
  const { user } = useAuth();
  const [financeData, setFinanceData] = useState<FinanceData>({
    revenue: 0,
    expenses: 0,
    profit: 0,
    revenueChange: 0,
    expensesChange: 0,
    lastUpdated: new Date()
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const fetchFinanceData = async () => {
      try {
        setLoading(true);
        
        // Get all-time data to match ROI tracker behavior
        const { data: allData, error: allError } = await supabase
          .from('roi_entries')
          .select('type, amount, date, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (allError) throw allError;

        // Calculate all-time totals
        const totalRevenue = allData
          ?.filter(entry => entry.type === 'revenue')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;
          
        const totalExpenses = allData
          ?.filter(entry => entry.type === 'expense')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;

        // Get last 30 days for change calculation
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recentRevenue = allData
          ?.filter(entry => {
            const entryDate = new Date(entry.date || entry.created_at);
            return entry.type === 'revenue' && entryDate >= thirtyDaysAgo;
          })
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;
          
        const recentExpenses = allData
          ?.filter(entry => {
            const entryDate = new Date(entry.date || entry.created_at);
            return entry.type === 'expense' && entryDate >= thirtyDaysAgo;
          })
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;

        // Get previous 30 days (30-60 days ago) for comparison
        const sixtyDaysAgo = new Date();
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
        
        const prevRevenue = allData
          ?.filter(entry => {
            const entryDate = new Date(entry.date || entry.created_at);
            return entry.type === 'revenue' && entryDate >= sixtyDaysAgo && entryDate < thirtyDaysAgo;
          })
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;
          
        const prevExpenses = allData
          ?.filter(entry => {
            const entryDate = new Date(entry.date || entry.created_at);
            return entry.type === 'expense' && entryDate >= sixtyDaysAgo && entryDate < thirtyDaysAgo;
          })
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;

        // Calculate percentage changes (handle division by zero)
        const revenueChange = prevRevenue !== 0 
          ? ((recentRevenue - prevRevenue) / prevRevenue) * 100 
          : recentRevenue > 0 ? 100 : 0;
          
        const expensesChange = prevExpenses !== 0 
          ? ((recentExpenses - prevExpenses) / prevExpenses) * 100 
          : recentExpenses > 0 ? 100 : 0;

        setFinanceData({
          revenue: totalRevenue,
          expenses: totalExpenses,
          profit: totalRevenue - totalExpenses,
          revenueChange: Math.round(revenueChange * 100) / 100,
          expensesChange: Math.round(expensesChange * 100) / 100,
          lastUpdated: new Date()
        });

      } catch (err) {
        console.error('Error fetching finance data:', err);
        setError('Failed to load financial data');
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceData();

    // Set up real-time subscription
    const subscription = supabase
      .channel('roi_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'roi_entries',
          filter: `user_id=eq.${user?.id}`
        }, 
        () => fetchFinanceData()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  return { financeData, loading, error };
};
